import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  endOfDay,
  endOfMonth,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import {
  getAwardRecord,
  listAwardRecordGameweeks,
  listAwardRecords,
} from "../utils/deliveryRecordsApi";
import { getCurrentUser, getUserPermissions } from "../utils/permissionsUtils";
import AwardRecalculatorDialog from "../sections/delivery-records/AwardRecalculatorDialog";

// The re-calculator is limited to these admin roles, like the currency one.
const canRecalculateRole = () =>
  getUserPermissions().isSuperAdmin || getCurrentUser()?.role === "operator";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const STATUSES = ["pending", "sending", "sent", "uncertain", "failed"];

const statusColors = {
  pending: "default",
  sending: "info",
  sent: "success",
  uncertain: "warning",
  failed: "error",
};

/** Gameweek awards repeat once per gameweek; lifetime ones are awarded once. */
const SCOPES = [
  { value: "gameweek", label: "Gameweek" },
  { value: "lifetime", label: "Lifetime" },
];

/**
 * Award ids by scope. Gameweek awards repeat once per gameweek; lifetime ones
 * are awarded once. team_over_120_points is the 200+ tier (it keeps its
 * original Gaming Hub id); team_over_200_points is retired but still has rows.
 */
const EVENT_GROUPS = [
  {
    label: "Gameweek",
    ids: [
      "team_over_100_points",
      "team_over_150_points",
      "team_over_120_points",
      "team_over_200_points",
      "all_strikers_scored",
      "all_defenders_clean_sheet",
      "all_midfielders_assist",
      "hat_trick",
      "striker_5_shots_on_target",
      "top_20pct_leaderboard",
      "top_10pct_leaderboard",
    ],
  },
  {
    label: "Lifetime",
    ids: [
      "create_team",
      "create_mini_league",
      "share_mini_league",
      "join_mini_league",
      "make_transfer",
    ],
  },
];

const PAGE_SIZES = [10, 20, 50, 100];

/** Compact sizing so the table fits on one screen. */
const compactField = {
  "& .MuiInputBase-root": { fontSize: 13 },
  "& .MuiInputLabel-root": { fontSize: 13 },
  "& .MuiFormHelperText-root": { fontSize: 11 },
};
const compactTable = {
  "& .MuiTableCell-root": { fontSize: 12, py: 0.5, px: 1 },
  "& .MuiTableCell-head": { fontWeight: 600 },
  "& .MuiChip-root": { height: 20, fontSize: 11 },
};

/**
 * Date range presets over the created time; a preset's `range()` is computed
 * when it is selected. The last 7 days is the default; looking up an older
 * gameweek needs a wider range.
 */
const DATE_PRESETS = [
  { value: "any", label: "Any time", range: () => [null, null] },
  {
    value: "today",
    label: "Today",
    range: () => [startOfDay(new Date()), endOfDay(new Date())],
  },
  {
    value: "yesterday",
    label: "Yesterday",
    range: () => {
      const yesterday = subDays(new Date(), 1);
      return [startOfDay(yesterday), endOfDay(yesterday)];
    },
  },
  {
    value: "last7",
    label: "Last 7 days",
    range: () => [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())],
  },
  {
    value: "last30",
    label: "Last 30 days",
    range: () => [startOfDay(subDays(new Date(), 29)), endOfDay(new Date())],
  },
  {
    value: "thisMonth",
    label: "This month",
    range: () => [startOfMonth(new Date()), endOfDay(new Date())],
  },
  {
    value: "lastMonth",
    label: "Last month",
    range: () => {
      const lastMonth = subMonths(new Date(), 1);
      return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
    },
  },
  { value: "custom", label: "Custom range" },
];

const COLUMNS = [
  { id: "created_at", label: "Created", sortable: true },
  { id: "event_id", label: "Award", sortable: true },
  { id: "source", label: "Source" },
  { id: "team", label: "Team" },
  { id: "user", label: "User" },
  { id: "status", label: "Status", sortable: true },
  { id: "attempt_count", label: "Attempts", sortable: true },
  { id: "sent_at", label: "Sent", sortable: true },
];

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const gameweekLabel = (gameweek) =>
  gameweek.name
    ? `GW${gameweek.number} · ${gameweek.name}`
    : `GW${gameweek.number}`;

/** A gameweek award shows its gameweek; any other award its source type. */
const sourceLabel = (record) =>
  record.gameweek_number != null
    ? `GW${record.gameweek_number}`
    : record.source_type;

const DetailField = ({ label, children }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
      {children ?? "—"}
    </Typography>
  </Grid>
);

const JsonBlock = ({ label, value }) => (
  <Box>
    <Typography variant="subtitle2" gutterBottom>
      {label}
    </Typography>
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        bgcolor: "grey.100",
        borderRadius: 1,
        fontSize: 12,
        overflowX: "auto",
      }}
    >
      {value ? JSON.stringify(value, null, 2) : "—"}
    </Box>
  </Box>
);

const AwardRecordsPage = () => {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalIsCapped, setTotalIsCapped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameweeks, setGameweeks] = useState([]);
  const [gameweeksLoading, setGameweeksLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sort, setSort] = useState({ column: "created_at", order: "desc" });
  const [selected, setSelected] = useState(null);
  const [recalculatorOpen, setRecalculatorOpen] = useState(false);
  const canRecalculate = useMemo(canRecalculateRole, []);
  // The list leaves the payload out; it is loaded when a record is opened.
  const [detail, setDetail] = useState({
    loading: false,
    error: null,
    record: null,
  });
  const openedRecordId = useRef(null);

  const [filters, setFilters] = useState({
    userOrTeamId: "",
    scope: "",
    eventId: "",
    gameweek: null,
    status: "",
    datePreset: "last7",
    customFrom: startOfDay(subDays(new Date(), 6)),
    customTo: endOfDay(new Date()),
  });
  const [debouncedUserOrTeamId, setDebouncedUserOrTeamId] = useState("");

  const userOrTeamIdInput = filters.userOrTeamId.trim();
  const userOrTeamIdIsInvalid =
    userOrTeamIdInput !== "" && !UUID_PATTERN.test(userOrTeamIdInput);

  useEffect(() => {
    const timer = setTimeout(
      () =>
        setDebouncedUserOrTeamId(
          UUID_PATTERN.test(userOrTeamIdInput) ? userOrTeamIdInput : ""
        ),
      500
    );
    return () => clearTimeout(timer);
  }, [userOrTeamIdInput]);

  useEffect(() => {
    listAwardRecordGameweeks()
      .then(setGameweeks)
      .catch((err) => {
        console.error("Failed to fetch award gameweeks:", err);
        setError("Failed to load the gameweek list.");
      })
      .finally(() => setGameweeksLoading(false));
  }, []);

  const dateRange = useMemo(() => {
    if (filters.datePreset === "custom") {
      return [
        filters.customFrom ? startOfDay(filters.customFrom) : null,
        filters.customTo ? endOfDay(filters.customTo) : null,
      ];
    }
    const preset = DATE_PRESETS.find(
      (item) => item.value === filters.datePreset
    );
    return preset.range();
  }, [filters.datePreset, filters.customFrom, filters.customTo]);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [from, to] = dateRange;
      const result = await listAwardRecords({
        userOrTeamId: debouncedUserOrTeamId,
        scope: filters.scope,
        eventId: filters.eventId,
        gameweekId: filters.gameweek?.id ?? "",
        status: filters.status,
        from: from && !Number.isNaN(from.getTime()) ? from.toISOString() : "",
        to: to && !Number.isNaN(to.getTime()) ? to.toISOString() : "",
        sort: sort.column,
        order: sort.order,
        page: page + 1,
        limit: rowsPerPage,
      });
      setRecords(result.awards);
      setTotal(result.total);
      setTotalIsCapped(result.totalIsCapped);
    } catch (err) {
      console.error("Failed to fetch award records:", err);
      setError("Failed to load award records. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [
    dateRange,
    debouncedUserOrTeamId,
    filters.scope,
    filters.eventId,
    filters.gameweek,
    filters.status,
    sort,
    page,
    rowsPerPage,
  ]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleSort = (column) => {
    setSort((prev) => ({
      column,
      order: prev.column === column && prev.order === "desc" ? "asc" : "desc",
    }));
    setPage(0);
  };

  const openRecord = async (record) => {
    openedRecordId.current = record.id;
    setSelected(record);
    setDetail({ loading: true, error: null, record: null });
    try {
      const full = await getAwardRecord(record.id);
      // A slower response for a previously opened row must not overwrite this one.
      if (openedRecordId.current !== record.id) return;
      setDetail({ loading: false, error: null, record: full });
    } catch (err) {
      if (openedRecordId.current !== record.id) return;
      console.error("Failed to fetch award record:", err);
      setDetail({
        loading: false,
        error: "Failed to load the payload. Please try again.",
        record: null,
      });
    }
  };

  const renderDetailDialog = () => {
    if (!selected) return null;

    return (
      <Dialog open onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>Award record</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Chip label={sourceLabel(selected)} size="small" />
            <Chip
              label={selected.status}
              color={statusColors[selected.status] || "default"}
              size="small"
            />
          </Box>

          <Grid container spacing={2}>
            <DetailField label="Record ID">{selected.id}</DetailField>
            <DetailField label="Award">{selected.event_id}</DetailField>
            <DetailField label="Source type">
              {selected.source_type}
            </DetailField>
            <DetailField label="Source ID">{selected.source_id}</DetailField>
            <DetailField label="Team">{selected.team_name}</DetailField>
            <DetailField label="Team ID">{selected.team_id}</DetailField>
            <DetailField label="Username">{selected.username}</DetailField>
            <DetailField label="User ID">{selected.user_id}</DetailField>
            <DetailField label="Gaming Hub user ID">
              {selected.engagecraft_id}
            </DetailField>
            <DetailField label="Attempts">{selected.attempt_count}</DetailField>
            <DetailField label="Qualified">
              {formatDate(selected.qualified_at)}
            </DetailField>
            <DetailField label="Created">
              {formatDate(selected.created_at)}
            </DetailField>
            <DetailField label="Updated">
              {formatDate(selected.updated_at)}
            </DetailField>
            <DetailField label="Send attempted">
              {formatDate(selected.send_attempted_at)}
            </DetailField>
            <DetailField label="Sent">
              {formatDate(selected.sent_at)}
            </DetailField>
            <DetailField label="SQS message ID">
              {selected.sqs_message_id}
            </DetailField>
            <DetailField label="Next attempt">
              {formatDate(selected.next_attempt_at)}
            </DetailField>
            <DetailField label="Lease expires">
              {formatDate(selected.lease_expires_at)}
            </DetailField>
            <DetailField label="Last error">{selected.last_error}</DetailField>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {detail.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : detail.error ? (
            <Alert severity="error">{detail.error}</Alert>
          ) : (
            <JsonBlock
              label="Payload for Gaming Hub"
              value={detail.record?.payload}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      <Helmet>
        <title>Gamification Awards | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" gutterBottom>
              Gamification Awards
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Every achievement queued for Gaming Hub, with its delivery status.
              Click any row to see the payload sent.
            </Typography>
          </Box>
          {canRecalculate && (
            <Button
              variant="outlined"
              onClick={() => setRecalculatorOpen(true)}
              sx={{ flexShrink: 0 }}
            >
              Re-calculate GW awards
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  "& .MuiTextField-root": compactField,
                }}
              >
                <TextField
                  label="User or team ID"
                  value={filters.userOrTeamId}
                  onChange={(e) =>
                    handleFilterChange("userOrTeamId", e.target.value)
                  }
                  error={userOrTeamIdIsInvalid}
                  helperText={
                    userOrTeamIdIsInvalid
                      ? "Enter a full user or team UUID"
                      : " "
                  }
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  sx={{ minWidth: 340 }}
                  size="small"
                />

                <Autocomplete
                  options={gameweeks}
                  loading={gameweeksLoading}
                  value={filters.gameweek}
                  onChange={(event, value) =>
                    handleFilterChange("gameweek", value)
                  }
                  getOptionLabel={gameweekLabel}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  size="small"
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Gameweek" />
                  )}
                />

                <TextField
                  select
                  label="Award"
                  value={filters.eventId}
                  onChange={(e) =>
                    handleFilterChange("eventId", e.target.value)
                  }
                  sx={{ minWidth: 220 }}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {EVENT_GROUPS.flatMap((group) => [
                    <ListSubheader key={group.label}>
                      {group.label}
                    </ListSubheader>,
                    ...group.ids.map((id) => (
                      <MenuItem key={id} value={id}>
                        {id}
                      </MenuItem>
                    )),
                  ])}
                </TextField>

                <TextField
                  select
                  label="Scope"
                  value={filters.scope}
                  onChange={(e) => handleFilterChange("scope", e.target.value)}
                  sx={{ minWidth: 140 }}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {SCOPES.map((scope) => (
                    <MenuItem key={scope.value} value={scope.value}>
                      {scope.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  sx={{ minWidth: 130 }}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Created"
                  value={filters.datePreset}
                  onChange={(e) =>
                    handleFilterChange("datePreset", e.target.value)
                  }
                  sx={{ minWidth: 160 }}
                  size="small"
                >
                  {DATE_PRESETS.map((preset) => (
                    <MenuItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Per page"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  sx={{ minWidth: 100 }}
                  size="small"
                >
                  {PAGE_SIZES.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </TextField>

                {filters.datePreset === "custom" && (
                  <>
                    <DatePicker
                      label="From"
                      value={filters.customFrom}
                      maxDate={filters.customTo || undefined}
                      onChange={(value) =>
                        handleFilterChange("customFrom", value)
                      }
                      inputFormat="dd/MM/yyyy"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          sx={{ width: 170 }}
                        />
                      )}
                    />
                    <DatePicker
                      label="To"
                      value={filters.customTo}
                      minDate={filters.customFrom || undefined}
                      onChange={(value) =>
                        handleFilterChange("customTo", value)
                      }
                      inputFormat="dd/MM/yyyy"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          sx={{ width: 170 }}
                        />
                      )}
                    />
                  </>
                )}
              </Box>
            </LocalizationProvider>
          </CardContent>
        </Card>

        <Card>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={compactTable}>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={
                        sort.column === column.id ? sort.order : false
                      }
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={sort.column === column.id}
                          direction={
                            sort.column === column.id ? sort.order : "desc"
                          }
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMNS.length}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : records.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMNS.length}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No award records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record) => (
                    <TableRow
                      key={record.id}
                      hover
                      onClick={() => openRecord(record)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDate(record.created_at)}
                      </TableCell>
                      <TableCell>{record.event_id}</TableCell>
                      <TableCell>{sourceLabel(record)}</TableCell>
                      <TableCell>
                        <Box sx={{ fontSize: 12 }}>
                          {record.team_name || "—"}
                        </Box>
                        <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                          {record.team_id ?? "—"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ fontSize: 12 }}>
                          {record.username || "—"}
                        </Box>
                        <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                          {record.engagecraft_id ?? "—"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={record.status}
                          color={statusColors[record.status] || "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{record.attempt_count}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDate(record.sent_at)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalIsCapped ? -1 : total}
            labelDisplayedRows={({ from, to }) =>
              `${from}–${to} of ${
                totalIsCapped
                  ? `more than ${total.toLocaleString("en-GB")}`
                  : total.toLocaleString("en-GB")
              }`
            }
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            // Page size is chosen with the "Per page" filter above the table.
            rowsPerPageOptions={[]}
            sx={{
              "& .MuiTablePagination-displayedRows": { fontSize: 12 },
            }}
          />
        </Card>
      </Container>

      {renderDetailDialog()}

      <AwardRecalculatorDialog
        open={recalculatorOpen}
        onClose={() => setRecalculatorOpen(false)}
      />
    </>
  );
};

export default AwardRecordsPage;
